<template>
  <div>
    <div v-if="$store.state.editorIsDemoVersion && deviceIsMobile && !hideMobileOverlay">
      <editor-mobile-guard />
    </div>
    <div v-else id="editor" class="d-flex flex-column">
      <div v-if="!loading" :class="{ blurry: startDemoVersion }">
        <toolbar
          v-if="$route.name !== 'login' && renderToolbar"
          @show-hubspot-form="showHubspotForm = true"
          @show-signup-form="showSignupForm()"
          @on-share-template="showShareForm()"
          @rerender-toolbar="rerenderToolbar()"
        />
      </div>
      <div v-if="loading" class="loading d-flex flex-column flex-grow-1 align-items-center justify-content-center">
        <h1>{{ $t("editor.loading-msg") }}</h1>
        <h2>{{ $t("editor.wait-msg") }}</h2>
        <logo-loader class="loader" />
      </div>

      <div
        v-else-if="
          ($store.state.templatePreview && $store.state.template) ||
            ($store.state.editorIsDemoVersion && $route.name.includes('demo'))
        "
        class="d-flex flex-grow-1"
        :class="{ blurry: startDemoVersion }"
      >
        <template-backup-banner v-if="$store.state.showingTemplateBackupInPreview"></template-backup-banner>
        <editor-content></editor-content>
        <settings-panel>panel</settings-panel>
      </div>
      <transition name="standard-fade">
        <EditorDemoCTA
          v-if="showEditorCta && $store.state.editorIsDemoVersion"
          @close="$store.commit('hideDemoCTA')"
          @show-hubspot-form="showHubspotForm = true"
          @show-signup-form="showSignupForm()"
        />
      </transition>
      <HubSpotModal v-if="showHubspotForm" @close="showHubspotForm = false" />
    </div>
  </div>
</template>

<script>
import LogoLoader from "@/components/LogoLoader";
import TemplateBackupBanner from "@/components/TemplateBackupBanner";
import EditorContent from "@/components/EditorContent";
import SettingsPanel from "@/components/SettingsPanel";
import ConfirmModal from "@/components/modals/ConfirmModal";
import LeaveEditor from "@/components/modals/LeaveEditor";
import Toolbar from "@/components/Toolbar";
import EditorDemoCTA from "@/components/EditorDemoCTA";
import StartDemoModal from "@/components/modals/StartDemoModal";
import HubSpotModal from "@/components/modals/HubSpotModal";
import DemoSignUpModal from "@/components/modals/DemoSignUpModal";
import ShareDemoModal from "@/components/modals/ShareDemoModal";
import EditorMobileGuard from "@/components/EditorMobileGuard";
import appHelper from "@/assets/js/appHelper.js";
export default {
  name: "Editor",
  components: {
    EditorContent,
    SettingsPanel,
    Toolbar,
    TemplateBackupBanner,
    LogoLoader,
    EditorDemoCTA,
    HubSpotModal,
    EditorMobileGuard
  },
  data() {
    return {
      isEditSaved: false,
      loading: false,
      startDemoVersion: false,
      showHubspotForm: false,
      deviceIsMobile: false,
      hideMobileOverlay: false,
      renderToolbar: true,
    };
  },
  computed: {
    ctaContent() {
      return this.$store.state.editorCtaContent;
    },
    showEditorCta() {
      return this.$store.state.showEditorCta;
    }
  },
  mounted() {
    if (this.$store.state.userDevice == "mobile") {
      this.deviceIsMobile = true;
    }
    this.loading = true;
    if (this.$route.name == "demo" || this.$route.name == "demo.signup") {
      if (this.$route.params.demokey) {
        this.$store.commit("setTemporaryDemoKey", this.$route.params.demokey);
        this.$store
          .dispatch("getDemoDataWithKey", this.$route.params.demokey)
          .then(() => {
            this.loading = false;
            this.$store.dispatch("getDemoFavorites")
          })
          .catch((e) => {
            this.toDemoRoute();
          });
      } else {
        this.toDemoRoute();
      }
    } else {
      this.$store.dispatch("getTemplateData", this.$route.params.templateId).then(
        () => {
          const language = localStorage.getItem(`ai-translation-language-${this.$route.params.templateId}`) || this.$store.state.template.language;
          this.$store.commit("setAiTranslationLanguage", {language: language, templateId: this.$store.state.template.metadata.id});
          this.loading = false;
        },
        (error) => {
          if (error === "notfound") this.$router.push("/");
          if (error.status === 500) {
            this.$modal.show(
              ConfirmModal,
              {
                title: this.$t("editor.load-error-title"),
                description:
                  this.$t("editor.load-error-desc1") +
                  (error.body.Message && error.body.Message.indexOf("datasource") > -1
                    ? this.$t("editor.load-error-desc2") + error.body.Message
                    : this.$t("editor.load-error-desc3")),
                confirmButtonText: "OK",
                hideCancelButton: true
              },
              { width: 350, height: "auto", adaptive: true }
            );
            this.$router.push("/");
          }
        }
      );
    }
  },
  beforeMount() {
    window.addEventListener("beforeunload", this.preventNav);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventNav);
  },
  methods: {
    preventNav(e) {
      if (!this.$store.state.unsavedChanges) return;
      e.preventDefault();
      return (e.returnValue = "Are you sure you want to exit?");
    },

    toDemoRoute() {
      this.$router.push({ name: "demo.signup" });
      this.loading = false;
      this.startDemoVersion = true;
      this.showDemoModal();
    },

    showDemoModal() {
      if (this.deviceIsMobile) return
        this.$modal.show(
          StartDemoModal,
          {
            onSubmit: (data) => {
              this.startDemoVersion = false;
            }
          },
          { minWidth: 300, maxWidth: 400, height: "auto", adaptive: true, clickToClose: false }
        );
      
    },

    showSignupForm() {
      this.$modal.show(
        DemoSignUpModal,
        {},
        {
          minWidth: 910,
          width: "60%",
          height: "auto",
          adaptive: true,
          resizable: true,
          clickToClose: false,
          scrollable: true
        }
      );
    },
    showShareForm() {
      this.$modal.show(ShareDemoModal, {}, { minWidth: 500, maxWidth: 750, height: "auto", adaptive: true });
    },

    rerenderToolbar() {
      this.renderToolbar = false;
      this.$nextTick(() => {
        this.renderToolbar = true;
      });
    },
  },
  created() {},
  beforeRouteLeave(to, from, next) {
    let onLeave = () => this.$store.commit("clearEditorState");
    if (!this.$store.state.unsavedChanges) {
      onLeave();
      next();
    } else {
      if (
        this.$modal.show(
          LeaveEditor,
          {
            thirdOption: true,
            title: this.$t("unsaved.title"),
            description: this.$t("unsaved.desc"),
            confirmButtonText: this.$t("unsaved.confirm"),
            thirdColorBtnPrimary: true,
            thirdOptionText: this.$t("sentence.save-quit"),
            onThirdOptionClick: (result) => {
              this.$store.dispatch("saveTemplateChanges").then(() => {
                onLeave();
                next();
              });
            },
            onConfirm: (result) => {
              this.$store.state.unsavedChanges = false;
              onLeave();
              next();
            },
            onCancel: (result) => {
              next(false);
            }
          },
          { width: 450, height: "auto", adaptive: true }
        )
        // confirm(
        //   'Du har ikke-gemte ændringer. Hvis du lukker skabelonen vil ændringerne gå tabt. Fortsæt?',
        //   )
      ) {
        // this.$store.state.unsavedChanges = false;
        // onLeave();
        // next();
      } else {
        // next(false);
      }
    }
  }
};
</script>

<style lang="less">
#editor {
  // flex: 1 1 auto;
  .loading {
    h1 {
      font-size: 26px;
      margin-bottom: 15px;
    }

    h2 {
      font-size: 18px;
      font-weight: normal;
    }
  }
  .loader {
    margin-top: 12px;
    width: 60px;
  }
  #editorContent {
    flex: 1 1 auto;
  }

  .blurry {
    filter: blur(4px);
  }
}

@media only screen and (max-width: 1180px) {
  body {
    touch-action: none;
    overflow: hidden;
  }
}
</style>
